<template>
  <div class="page flex-col">
    <AppHeader />

    <div class="ad-banner">
      <div class="ad-banner-mask">
        <span class="ad-banner-zh">{{ isEnglish ? 'Counter-UAS Products' : '反无人机产品' }}</span>
        <span class="ad-banner-en">{{ isEnglish ? 'Anti-drone products' : 'Anti drone products' }}</span>
      </div>
    </div>

    <div class="ad-main">
      <nav class="ad-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="ad-sep">&gt;</span>
        <router-link :to="listLink">
          {{ isEnglish ? 'Counter-UAS Products' : '反无人机产品' }}
        </router-link>
        <span class="ad-sep">&gt;</span>
        <router-link :to="listLink">{{ displayCategoryLabel }}</router-link>
        <span class="ad-sep">&gt;</span>
        <span class="ad-crumb-active">{{ displayName }}</span>
      </nav>

      <div class="ad-title-row">
        <span class="ad-title-zh">{{ isEnglish ? 'Counter-UAS Products' : '反无人机产品' }}</span>
        <span class="ad-title-en">PRODUCTS</span>
      </div>
      <div class="ad-title-line"></div>

      <div class="ad-body">
        <AntiDroneSidebar />

        <div class="ad-content">
          <h1 class="ad-product-name">
            <span class="ad-product-name-text">{{ displayName }}</span>
            <span v-if="displayModel" class="ad-product-model">{{ displayModel }}</span>
          </h1>

          <div v-if="displayIntro" class="ad-block">
            <div class="ad-block-label">{{ introLabel }}</div>
            <p class="ad-intro-text">{{ displayIntro }}</p>
          </div>

          <div class="ad-detail-row" :class="{ 'is-stack': !hasSpecs }">
            <div ref="heroImgWrap" class="ad-hero-img-wrap">
              <img class="ad-hero-img" :src="productImage" :alt="displayName" @load="syncSpecScrollHeight" />
            </div>
            <div v-if="hasSpecs" class="ad-spec-wrap">
              <div ref="specTitle" class="ad-spec-title">{{ specsTitle }}</div>
              <div ref="specScroll" class="ad-spec-scroll">
                <table class="ad-spec-table">
                  <tbody>
                    <tr v-for="(row, index) in displaySpecs" :key="'sp_' + index">
                      <th>{{ row.label }}</th>
                      <td>{{ row.value }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-if="displayFeatures.length" class="ad-block ad-features-block">
            <div class="ad-block-label">{{ featuresLabel }}</div>
            <ul class="ad-feature-list">
              <li
                v-for="(item, index) in displayFeatures"
                :key="'ft_' + index"
                class="ad-feature-item"
              >
                <div class="ad-feature-title">{{ item.title }}</div>
                <p class="ad-feature-desc">{{ item.desc }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AntiDroneSidebar from '@/components/AntiDroneSidebar.vue';
import { isEnglishRoute, withLang, siteHomeLink } from '@/utils/lang';

const HERO_IMG_MIN_HEIGHT = 450;

const fallbackDetail = {
  name: '低空监视雷达',
  nameEn: 'Low-Altitude Surveillance Radar',
  category: 'detect',
  categoryLabel: '侦测系列',
  categoryLabelEn: 'Detection Series',
  intro:
    '低空监视雷达是一款面向低空慢速、小目标（如无人机、鸟类等）的高性能探测设备。基于先进的二维相控阵电子扫描技术，可实现目标实时探测、连续跟踪、精准测量及多目标管理，为低空安全防护提供稳定可靠的目标感知能力。',
  introEn:
    'The low-altitude surveillance radar is a high-performance detection system for slow, small low-altitude targets such as drones and birds.',
  advantages: [
    { title: '精准探测', titleEn: 'Precise Detection', desc: '面向无人机、鸟类等低空慢速小目标，实现全天候远距离监测。', descEn: 'All-weather long-range monitoring of slow, small low-altitude targets.' },
    { title: '高精度测量', titleEn: 'High-Precision Measurement', desc: '提供目标距离、方位等高精度测量信息，为目标识别与跟踪提供可靠数据。', descEn: 'High-precision range and azimuth for identification and tracking.' },
    { title: '智能跟踪', titleEn: 'Intelligent Tracking', desc: '支持TWS和TAS两种工作模式，实现目标持续跟踪。', descEn: 'Supports TWS and TAS modes for continuous tracking.' },
    { title: '多目标处理', titleEn: 'Multi-Target Processing', desc: '支持多目标同步探测与管理，满足复杂低空环境监测需求。', descEn: 'Simultaneous multi-target detection and management.' },
    { title: '强抗干扰', titleEn: 'Strong Anti-Jamming', desc: '采用先进数字信号处理技术，在复杂环境下保持稳定探测性能。', descEn: 'Stable detection in complex electromagnetic environments.' },
    { title: '稳定可靠', titleEn: 'Stable & Reliable', desc: '模块化设计，维护便捷，适用于长期连续运行和固定部署。', descEn: 'Modular design for long-term continuous fixed deployment.' }
  ]
};

const defaultProductImg = require('../lanhu_7chanpinliebiao/assets/img/FigmaDDSSlicePNG89bbbef5e0cf9e9d2d061a45a5ca017b.png');

export default {
  name: 'AntiDroneProductDetail',
  components: { AntiDroneSidebar },
  data() {
    return {
      detailsMap: {},
      categories: []
    };
  },
  computed: {
    isEnglish() {
      return isEnglishRoute(this.$route);
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    listLink() {
      return {
        path: '/products',
        query: withLang({ cat: this.detail.category || 'detect' }, this.$route)
      };
    },
    productId() {
      return this.$route.query.id || 'radar';
    },
    productInfo() {
      return this.findProductInfo(this.productId);
    },
    productImage() {
      const info = this.productInfo;
      return (info && info.image) || defaultProductImg;
    },
    detail() {
      const fromMap = this.detailsMap[this.productId];
      if (fromMap) return this.enrichDetail(fromMap);
      if (this.detailsMap.default) {
        const base = this.enrichDetail(this.detailsMap.default);
        const nameInfo = this.productInfo;
        return {
          ...base,
          name: (nameInfo && nameInfo.name) || base.name,
          nameEn: (nameInfo && nameInfo.nameEn) || base.nameEn,
          category:
            this.$route.query.cat ||
            (nameInfo && nameInfo.category) ||
            base.category
        };
      }
      return fallbackDetail;
    },
    displayName() {
      if (this.isEnglish) return this.detail.nameEn || this.detail.name;
      return this.detail.name;
    },
    displayModel() {
      const info = this.productInfo;
      if (!info) return '';
      if (this.isEnglish) return info.modelEn || info.model || '';
      return info.model || info.modelEn || '';
    },
    displayCategoryLabel() {
      if (this.isEnglish) {
        return this.detail.categoryLabelEn || this.detail.categoryLabel;
      }
      return this.detail.categoryLabel;
    },
    introLabel() {
      return this.isEnglish ? 'Overview' : '产品简介';
    },
    featuresLabel() {
      return this.isEnglish ? 'Features' : '功能简介';
    },
    specsTitle() {
      return this.isEnglish ? 'Specifications' : '技术参数';
    },
    displayIntro() {
      const info = this.productInfo || {};
      if (this.isEnglish) {
        return info.introEn || info.intro || this.detail.introEn || this.detail.intro || '';
      }
      return info.intro || info.introEn || this.detail.intro || this.detail.introEn || '';
    },
    displayFeatures() {
      const info = this.productInfo || {};
      const raw = this.isEnglish
        ? info.featuresEn || info.features || []
        : info.features || info.featuresEn || [];
      if (!Array.isArray(raw)) return [];
      return raw
        .map((item) => {
          if (!item) return null;
          if (typeof item === 'string') return { title: '', desc: item };
          return {
            title: item.title || '',
            desc: item.desc || ''
          };
        })
        .filter((item) => item && (item.title || item.desc));
    },
    displaySpecs() {
      const info = this.productInfo || {};
      const raw = this.isEnglish
        ? info.specsEn || info.specs || []
        : info.specs || info.specsEn || [];
      return Array.isArray(raw) ? raw : [];
    },
    hasSpecs() {
      return this.displaySpecs.length > 0;
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.initSpecScrollSync();
  },
  beforeDestroy() {
    this.destroySpecScrollSync();
  },
  watch: {
    productId() {
      this.$nextTick(() => this.syncSpecScrollHeight());
    },
    displaySpecs() {
      this.$nextTick(() => this.syncSpecScrollHeight());
    },
    hasSpecs() {
      this.$nextTick(() => {
        this.destroySpecScrollSync();
        this.initSpecScrollSync();
      });
    }
  },
  methods: {
    async fetchData() {
      try {
        const res = await fetch('/data/antidrone.json');
        if (!res.ok) return;
        const data = await res.json();
        this.detailsMap = data.details || {};
        this.categories = data.categories || [];
        this.$nextTick(() => this.syncSpecScrollHeight());
      } catch (err) {
        console.warn('Failed to fetch antidrone.json:', err);
      }
    },
    findProductInfo(id) {
      for (const cat of this.categories) {
        const found = (cat.products || []).find((p) => p.id === id);
        if (found) return found;
      }
      return null;
    },
    enrichDetail(detail) {
      const catId = detail.category || this.$route.query.cat || 'detect';
      const cat = this.categories.find((c) => c.id === catId);
      return {
        ...detail,
        category: catId,
        categoryLabel:
          detail.categoryLabel || (cat && cat.label) || (cat && cat.name) || '侦测系列',
        categoryLabelEn:
          detail.categoryLabelEn ||
          (cat && cat.labelEn) ||
          (cat && cat.nameEn) ||
          'Detection Series'
      };
    },
    initSpecScrollSync() {
      this.$nextTick(() => {
        const wrap = this.$refs.heroImgWrap;
        if (!wrap || !this.hasSpecs) return;

        if (typeof ResizeObserver !== 'undefined') {
          this._heroResizeObserver = new ResizeObserver(() => {
            this.syncSpecScrollHeight();
          });
          this._heroResizeObserver.observe(wrap);
        }

        this._syncSpecScrollHeightHandler = () => this.syncSpecScrollHeight();
        window.addEventListener('resize', this._syncSpecScrollHeightHandler);
        this.syncSpecScrollHeight();
      });
    },
    destroySpecScrollSync() {
      if (this._heroResizeObserver) {
        this._heroResizeObserver.disconnect();
        this._heroResizeObserver = null;
      }
      if (this._syncSpecScrollHeightHandler) {
        window.removeEventListener('resize', this._syncSpecScrollHeightHandler);
        this._syncSpecScrollHeightHandler = null;
      }
    },
    syncSpecScrollHeight() {
      const wrap = this.$refs.heroImgWrap;
      const scroll = this.$refs.specScroll;
      const title = this.$refs.specTitle;
      if (!wrap || !scroll) return;

      if (window.innerWidth <= 1100) {
        scroll.style.maxHeight = '';
        return;
      }

      const titleHeight = title ? title.offsetHeight + 14 : 0;
      const heroHeight = Math.max(HERO_IMG_MIN_HEIGHT, wrap.offsetHeight);
      const maxScrollHeight = Math.max(160, heroHeight - titleHeight);
      scroll.style.maxHeight = `${maxScrollHeight}px`;
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

.ad-banner {
  width: 100%;
  height: 360px;
  margin-top: 8px;
  background: url(./assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.ad-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.ad-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.ad-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.ad-banner-en {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 500;
}

.ad-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 44px 0 72px;
  box-sizing: border-box;
}

.ad-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

.ad-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.ad-breadcrumb a:hover {
  color: #1e40af;
}

.ad-sep {
  color: #9ca3af;
}

.ad-crumb-active {
  color: #1e40af;
}

.ad-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.ad-title-zh {
  font-size: 32px;
  font-weight: 900;
  color: #1e40af;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-title-en {
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 1px;
}

.ad-title-line {
  width: 100%;
  height: 2px;
  background: #93c5fd;
  margin: 16px 0 40px;
}

.ad-body {
  width: 100%;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 56px;
  align-items: start;
}

.ad-content {
  min-width: 0;
}

.ad-product-name {
  margin: 4px 0 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px 14px;
  font-size: 26px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-product-name-text {
  color: inherit;
  font: inherit;
}

.ad-product-model {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: #2563eb;
  letter-spacing: 0.04em;
  font-family: "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  padding: 2px 10px;
  white-space: nowrap;
}

.ad-block {
  margin-bottom: 28px;
  width: 100%;
}

.ad-block-label {
  font-size: 18px;
  font-weight: 800;
  color: #1e40af;
  margin-bottom: 16px;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-intro-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.9;
  color: #374151;
  text-align: justify;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-features-block {
  margin-top: 36px;
  margin-bottom: 8px;
}

.ad-feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 16px;
}

.ad-feature-item {
  margin: 0;
  padding: 22px 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  box-sizing: border-box;
}

.ad-feature-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e40af;
  line-height: 1.5;
  margin-bottom: 8px;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-feature-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #4b5563;
  text-align: left;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-detail-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
  margin-top: 8px;
}

.ad-detail-row.is-stack {
  grid-template-columns: 1fr;
}

.ad-hero-img-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  border-radius: 8px;
  padding: 24px 16px;
  box-sizing: border-box;
  min-height: 450px;
}

.ad-hero-img {
  max-width: 100%;
  max-height: 420px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ad-spec-wrap {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.ad-spec-title {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  flex-shrink: 0;
}

.ad-spec-scroll {
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.ad-spec-scroll::-webkit-scrollbar {
  width: 6px;
}

.ad-spec-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.ad-spec-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.ad-spec-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
  line-height: 1.4;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-spec-table th,
.ad-spec-table td {
  padding: 12px 16px;
  border: none;
  vertical-align: middle;
}

.ad-spec-table th {
  width: 38%;
  text-align: left;
  font-weight: 600;
  color: #374151;
}

.ad-spec-table td {
  color: #4b5563;
  white-space: pre-line;
}

.ad-spec-table tr:nth-child(odd) th,
.ad-spec-table tr:nth-child(odd) td {
  background: #eef1f4;
}

.ad-spec-table tr:nth-child(even) th,
.ad-spec-table tr:nth-child(even) td {
  background: #ffffff;
}

@media (max-width: 1100px) {
  .ad-body {
    grid-template-columns: 1fr;
  }

  .ad-detail-row {
    grid-template-columns: 1fr;
  }

  .ad-spec-scroll {
    max-height: none !important;
  }
}

@media (max-width: 700px) {
  .ad-banner {
    height: 220px;
  }

  .ad-banner-zh {
    font-size: 30px;
  }

  .ad-product-name {
    font-size: 22px;
  }

  .ad-feature-list {
    grid-template-columns: 1fr;
  }
}
</style>
